import {
  useWidgetByIdQuery,
  useWidgetUpdateByIdMutation,
  WidgetByIdDocument,
} from "gql/generated";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  WidgetDetailsPageActionKey,
  WidgetDetailsViewSections,
} from "./_widgetDetails.config";
import { useToastContext } from "@ttc3k/trekker";
import { getMultilingualString } from "shared/utils";
import { ListFilters } from "apps/customLists/components/filterScreens";
import { getDefaultFiltersFromListSelections } from "apps/customLists/components/utils";

export const useWidgetDetails = () => {
  const { t } = useTranslation();
  const { toastFactory } = useToastContext();

  // Load the widget details
  const { widgetId } = useParams();

  const [widgetFilters, setWidgetFilters] = useState<ListFilters>();
  const { data, error, loading, refetch } = useWidgetByIdQuery({
    variables: { id: widgetId },
    onCompleted: (data) => {
      setWidgetFilters(
        getDefaultFiltersFromListSelections(data.widgetById.list.selections),
      );
    },
  });
  const widget = data?.widgetById;

  // Handle resetting the filters after the source lists is updated
  // Previously this was handled in onCompleted of the original query,
  // but notifyOnNetworkStatusChange: true causes a full page refresh
  const handleRefetchWidgetDetails = async () => {
    const response = await refetch();
    setWidgetFilters(
      getDefaultFiltersFromListSelections(
        response.data.widgetById.list.selections,
      ),
    );
  };

  // The "Add to favorites" action becomes "Remove from favorites" is the widget is favorited
  // So, we watch for when changes to the widget to determine how to display that PageAction
  const [pageActionToDisable, setPageActionToDisable] = useState("");
  const [pageActions, setPageActions] = useState(WidgetDetailsViewSections);
  useEffect(() => {
    setPageActions((prev) =>
      prev.map((item) =>
        item.key == WidgetDetailsPageActionKey.AddToFavorites
          ? {
              ...item,
              useAlternateInfo: data?.widgetById.favorited,
            }
          : item.key === WidgetDetailsPageActionKey.ArchiveWidget
            ? {
                ...item,
                useAlternateInfo: data?.widgetById.archived,
              }
            : item,
      ),
    );
  }, [data?.widgetById]);

  // Handle showing/hiding of the list editing modal
  const [isListEditingModalOpen, setIsListEditingModalOpen] = useState(false);
  const handleEditListItemsClick = () =>
    setIsListEditingModalOpen((prev) => !prev);

  // Handle showing/hiding of the widget embedding modal
  const [isEmbedModalOpen, setIsEmbedModalOpen] = useState(false);

  const handleActionClick = (key: string) => {
    setPageActionToDisable(key);
    switch (key) {
      case WidgetDetailsPageActionKey.PreviewWidget:
        setPageActionToDisable("");
        setIsPreviewWidgetModalOpen(true);
        break;
      case WidgetDetailsPageActionKey.AddToFavorites:
        favoriteWidget();
        break;
      case WidgetDetailsPageActionKey.ArchiveWidget:
        archiveWidget();
        break;
    }
  };

  // Handle adding to / removing from favorites
  const [widgetUpdateByIdMutation] = useWidgetUpdateByIdMutation();
  const favoriteWidget = () => {
    if (!widget) {
      return;
    }
    const favorited = !widget?.favorited;
    widgetUpdateByIdMutation({
      variables: {
        id: widget._id,
        record: {
          favorited,
        },
      },
      refetchQueries: [WidgetByIdDocument],
    })
      .then((response) => {
        if (!response.errors) {
          toastFactory.create({
            title: t("core:SUCCESS"),
            description: t("core:FAVORITES.ACTION.SUCCESS", {
              addedOrRemoved: favorited
                ? t("core:FAVORITES.ACTION.ADDED_TO")
                : t("core:FAVORITES.ACTION.REMOVED_FROM"),
              listName: getMultilingualString(widget.name ?? {}),
            }),
          });
        } else {
          toastFactory.create({
            title: t("core:ERROR.TITLE"),
            description: `${t("core:FAVORITES.ACTION.ERROR", { listName: getMultilingualString(widget.name) })}. ${t("core:FORM_ERRORS.PLEASE_TRY_AGAIN")}`,
          });
        }
      })
      .catch(() => {
        toastFactory.create({
          title: t("core:ERROR.TITLE"),
          description: `${t("core:FAVORITES.ACTION.ERROR", { listName: getMultilingualString(widget.name) })}. ${t("core:FORM_ERRORS.PLEASE_TRY_AGAIN")}`,
        });
      })
      .finally(() => setPageActionToDisable(""));
  };

  /**
   * Achive/Unarchive a widget
   * @returns
   */
  const archiveWidget = () => {
    if (!widget) {
      return;
    }
    const archived = !widget.archived;
    widgetUpdateByIdMutation({
      variables: {
        id: widget._id,
        record: {
          archived,
        },
      },
      refetchQueries: [WidgetByIdDocument],
    })
      .then((response) => {
        if (!response.errors) {
          toastFactory.create({
            title: t("core:SUCCESS"),
            description: t("core:ARCHIVE.ACTION.SUCCESS", {
              archivedOrUnarchived: archived
                ? t("core:ARCHIVE.ACTION.ARCHIVED")
                : t("core:ARCHIVE.ACTION.UNARCHIVED"),
              entityName: getMultilingualString(widget.name ?? {}),
            }),
          });
        } else {
          toastFactory.create({
            title: t("core:ERROR.TITLE"),
            description: `${t("core:ARCHIVE.ACTION.ERROR", { entityName: getMultilingualString(widget.name) })}. ${t("core:FORM_ERRORS.PLEASE_TRY_AGAIN")}`,
          });
        }
      })
      .catch(() => {
        toastFactory.create({
          title: t("core:ERROR.TITLE"),
          description: `${t("core:ARCHIVE.ACTION.ERROR", { entityName: getMultilingualString(widget.name) })}. ${t("core:FORM_ERRORS.PLEASE_TRY_AGAIN")}`,
        });
      })
      .finally(() => setPageActionToDisable(""));
  };

  const [isPreviewWidgetModalOpen, setIsPreviewWidgetModalOpen] =
    useState(false);

  return {
    loading,
    error,
    widget,
    isListEditingModalOpen,
    isEmbedModalOpen,
    pageActions,
    pageActionToDisable,
    widgetFilters,
    handleActionClick,
    handleEditListItemsClick,
    setIsEmbedModalOpen,
    refetchWidget: handleRefetchWidgetDetails,
    isPreviewWidgetModalOpen,
    setIsPreviewWidgetModalOpen,
  };
};
